import React from 'react';
import './OptimizationDatasetPicker.css';

interface OptimizationDatasetPickerProps {
  datasets: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  /** Single choice (radio) vs multiple (checkboxes). Default: multiple. */
  selectionMode?: 'single' | 'multiple';
}

const OptimizationDatasetPicker: React.FC<OptimizationDatasetPickerProps> = ({
  datasets,
  selected,
  onChange,
  disabled = false,
  loading = false,
  emptyMessage = 'Датасетов нет',
  className = '',
  selectionMode = 'multiple',
}) => {
  const isSingle = selectionMode === 'single';

  const toggleDataset = (name: string) => {
    if (disabled) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    if (isSingle) {
      onChange([trimmed]);
      return;
    }
    if (selected.includes(trimmed)) {
      onChange(selected.filter((x) => x !== trimmed));
    } else {
      onChange([...selected, trimmed]);
    }
  };

  const selectAll = () => {
    if (disabled || datasets.length === 0 || isSingle) return;
    onChange([...datasets]);
  };

  const clearAll = () => {
    if (disabled || isSingle) return;
    onChange([]);
  };

  const showList = datasets.length > 0;
  const selectedSingle = isSingle ? selected[0] ?? '' : '';

  return (
    <div
      className={`opt-dataset-picker ${isSingle ? 'opt-dataset-picker--single' : ''} ${className}`.trim()}
      role="group"
      aria-label="Датасеты"
    >
      <div className="opt-dataset-picker__list-wrap">
        {showList ? (
          <ul
            className="opt-dataset-picker__list"
            role={isSingle ? 'radiogroup' : 'listbox'}
            aria-multiselectable={isSingle ? undefined : true}
          >
            {datasets.map((name) => {
              const id = `opt-ds-${name.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
              const checked = isSingle ? selectedSingle === name : selected.includes(name);
              return (
                <li key={name}>
                  <label
                    className={`opt-dataset-picker__row${checked ? ' opt-dataset-picker__row--selected' : ''}${
                      disabled ? ' opt-dataset-picker__row--disabled' : ''
                    }`}
                  >
                    <input
                      type={isSingle ? 'radio' : 'checkbox'}
                      id={id}
                      name={isSingle ? 'opt-dataset-single' : undefined}
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleDataset(name)}
                    />
                    <span className="opt-dataset-picker__name" title={name}>
                      {name}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="opt-dataset-picker__empty">
            {loading ? 'Loading datasets…' : emptyMessage}
          </div>
        )}
        {loading && showList ? (
          <div className="opt-dataset-picker__loading-overlay" aria-hidden>
            <span className="opt-dataset-picker__loading-text">Обновляем…</span>
          </div>
        ) : null}
      </div>

      {showList && !isSingle ? (
        <div className="opt-dataset-picker__footer">
          <div className="opt-dataset-picker__actions">
            <button type="button" className="opt-dataset-picker__action" disabled={disabled} onClick={selectAll}>
              Выбрать все
            </button>
            <span className="opt-dataset-picker__action-sep" aria-hidden>
              ·
            </span>
            <button type="button" className="opt-dataset-picker__action" disabled={disabled} onClick={clearAll}>
              Снять выбор
            </button>
          </div>
          <p className="opt-dataset-picker__hint">
            {selected.length} of {datasets.length} selected — check any number of datasets
          </p>
        </div>
      ) : null}

      {showList && isSingle ? (
        <p className="opt-dataset-picker__hint opt-dataset-picker__hint--single">
          {selectedSingle ? `Selected: ${selectedSingle}` : 'Выберите один датасет оценок'}
        </p>
      ) : null}
    </div>
  );
};

export default OptimizationDatasetPicker;
